// Host ledger (DESIGN_SPEC §12.8, §11.4.8). Joins DNS answers (names for addresses), the TLS proxy (net.http:
// method, path, status, bytes), guest connections (net.conn, with pid) and the host capture (net.flow, no pid)
// by destination address, and lays out the Network figure's other tables from the same pass.
//   buildLedger(events, opts?) → { hosts, http, dns, conns, flows }   T0-relative times, busiest first
//   buildHosts(events, opts?)  → HostLedgerRow[]                        one row per host name (or bare address)
//   hostSignals(row)           → HostSignal[]                           facts for the host's ThreatTag
//   ledgerOf(ctx)              → NetLedger                              the report's ledger, memoized per job snapshot
// A host is background when every event behind it is OS background (noise.ts); `party` says who reached it:
// the traced sample subtree, only processes outside it, or nobody the guest named (host capture / proxy only).
import { bytes } from "@/lib/format";
import { classifyNoise, splitHostPort } from "@/lib/derive/noise";
import type { CiteIndex, HostRow, HttpReq, NoiseIndex, NoiseService, ReportCtx } from "@/lib/derive/types";
import type { Event, Severity } from "@/types";
import { cadenceOf, cadenceText, link, namesOf, noiseOf, num, procResolver, str, t0Of, tsOf } from "./fold";

type BuildHostsOptions = {
  /** Epoch ms of T0 (contacts and first-seen are relative to it); null → the first event. */
  t0?: number | null;
  /** Shared background index; computed from `events` when absent. */
  noise?: NoiseIndex;
  /** Process name for a pid at a seq (the tree's view); falls back to proc.start images in `events`. */
  procName?: (pid: number, seq: number) => string | undefined;
  /** Is this pid (at this seq) in the traced sample subtree? Defaults to proc.start with scope ≠ false. */
  inScope?: (pid: number, seq: number) => boolean;
  /** Completed documents: tags citing a host's events tint its ThreatTag. */
  cite?: CiteIndex;
  sample?: string;
};

type Party = "sample" | "other" | "none";
type Proc = { name: string; pid: number; count: number };

/** Suricata signature that fired on a flow matching this row's destination. */
export type SigRef = {
  signatureId: number;
  signature: string;
  category: string;
  /** Suricata severity 1-4; 1 is highest. */
  severity: number;
  appProto?: string;
  flowId?: number;
  destPort?: number;
};

export type HostLedgerRow = HostRow & {
  /** CNAME targets the resolver answered with */
  via: string[];
  lookups: number;
  procs: Proc[];
  party: Party;
  /** HTTP requests through the proxy */
  httpCount: number;
  /** schemes seen through the proxy */
  schemes: string[];
  /** the largest single request body */
  maxSent: number;
  /** severity of the most severe tag citing the host's events (documents only), and those tags */
  citeSev?: Severity;
  citeTags: string[];
  /** Suricata signatures firing on any of this host's addresses. Deduped by signature_id. Highest-severity first. */
  sigs: SigRef[];
};

export type HttpRow = {
  key: string;
  method: string;
  scheme: string;
  host: string;
  path: string;
  url: string;
  statuses: number[];
  count: number;
  sent: number;
  received: number;
  sized: boolean;
  cadenceSec?: number;
  firstT: number;
  process: { name: string; pid: number } | null;
  background: boolean;
  service?: NoiseService;
  seqs: number[];
  sigs: SigRef[];
  /** Base64 request / response bytes from the TLS MITM proxy's capture.
   *  The row aggregates N requests to the same (method, url); we keep the
   *  LAST non-empty sample seen so re-polls of /gate.php show the most
   *  recent body. truncated=true when the proxy capped at 64 KiB. */
  reqBodyB64?: string;
  respBodyB64?: string;
  reqBodyTruncated?: boolean;
  respBodyTruncated?: boolean;
  /** Total non-truncated body size in bytes (from req_bytes / resp_bytes on
   *  the matching event — the full size even when the embedded body was
   *  truncated to 64 KiB). */
  reqBodyFullSize?: number;
  respBodyFullSize?: number;
};

export type DnsRow = {
  key: string;
  name: string;
  qtype: string;
  answers: string[];
  via: string[];
  lookups: number;
  cadenceSec?: number;
  firstT: number;
  contacted: boolean;
  background: boolean;
  service?: NoiseService;
  seqs: number[];
};

export type ConnRow = {
  key: string;
  pid?: number;
  name?: string;
  dst: string;
  ip: string;
  port?: number;
  host: string | null;
  proto: string;
  op: string;
  count: number;
  firstT: number;
  cadenceSec?: number;
  mirrored: number;
  background: boolean;
  service?: NoiseService;
  seqs: number[];
  sigs: SigRef[];
};

export type FlowRow = {
  key: string;
  proto: string;
  ip: string;
  dport?: number;
  src: string;
  count: number;
  bytes: number;
  sized: boolean;
  firstT: number;
  /** flows the guest also reported as a connection */
  mirrored: number;
  host: string | null;
  background: boolean;
  service?: NoiseService;
  seqs: number[];
  sigs: SigRef[];
};

type NetLedger = { hosts: HostLedgerRow[]; http: HttpRow[]; dns: DnsRow[]; conns: ConnRow[]; flows: FlowRow[] };

const lower = (s: string) => s.trim().toLowerCase().replace(/\.$/, "");
const isIp = (s: string) => /^\d{1,3}(\.\d{1,3}){3}$/.test(s) || (s.includes(":") && /^[0-9a-f:.]+$/i.test(s));
const DEDUPE_MS = 50;

/** The most common background service among seqs, and whether every one of them is background. */
function bgOf(seqs: readonly number[], noise: NoiseIndex): { background: boolean; service?: NoiseService } {
  if (!seqs.length) return { background: false };
  const counts = new Map<NoiseService, number>();
  let bg = 0;
  for (const s of seqs) {
    const h = noise.hits.get(s);
    if (!h) continue;
    bg++;
    counts.set(h.service, (counts.get(h.service) ?? 0) + 1);
  }
  let service: NoiseService | undefined;
  let top = 0;
  for (const [s, n] of counts) if (n > top) [service, top] = [s, n];
  return bg === seqs.length ? { background: true, service } : { background: false };
}

function dedupe(times: number[]): number[] {
  const s = [...times].sort((a, b) => a - b);
  const out: number[] = [];
  for (const t of s) if (!out.length || t - out[out.length - 1] > DEDUPE_MS) out.push(t);
  return out;
}

type HostAcc = {
  key: string;
  host: string | null;
  addrs: Set<string>;
  ports: Set<number>;
  procs: Map<number, Proc>;
  reqs: Map<string, HttpReq & { times: number[] }>;
  conn: number;
  flow: number;
  sent: number;
  received: number;
  maxSent: number;
  contacts: number[];
  seqs: number[];
  schemes: Set<string>;
  inScope: boolean;
  outOfScope: boolean;
};

export function buildLedger(events: readonly Event[], opts: BuildHostsOptions = {}): NetLedger {
  const empty: NetLedger = { hosts: [], http: [], dns: [], conns: [], flows: [] };
  if (!events.length) return empty;
  const noise = opts.noise ?? classifyNoise(events);
  const links = link(events);
  const procName = opts.procName ?? procResolver(events, opts.sample);
  const firstAt = tsOf(events[0], 0);
  const t0 = opts.t0 ?? firstAt;

  // Who is traced: proc.start with scope ≠ false (the agent's launch record counts).
  const scoped = new Set<number>();
  if (!opts.inScope) {
    for (const e of events) {
      if (e.kind !== "proc.start") continue;
      const pid = num(e.fields?.pid);
      const scope = e.fields?.scope;
      if (pid !== undefined && scope !== false && scope !== "false") scoped.add(pid);
    }
  }
  const inScope = opts.inScope ?? ((pid: number) => scoped.has(pid));

  // Names for addresses, over time: an address belongs to the name most recently resolved to it.
  const answers = new Map<string, { name: string; at: number }[]>();
  const dnsRows = new Map<string, DnsRow & { qtimes: number[]; ips: Set<string> }>();
  let at = firstAt;
  for (const e of events) {
    at = tsOf(e, at);
    if (e.kind !== "net.dns") continue;
    const f = e.fields ?? {};
    const owner = links.dnsOf.get(e.seq) ?? lower(str(f.qname ?? f.name));
    if (!owner) continue;
    let row = dnsRows.get(owner);
    if (!row) {
      row = { key: owner, name: owner, qtype: "", answers: [], via: [], lookups: 0, firstT: at - t0, contacted: false, background: false, seqs: [], qtimes: [], ips: new Set() };
      dnsRows.set(owner, row);
    }
    row.seqs.push(e.seq);
    if (str(f.qname)) {
      if (f.qr !== true && f.qr !== "true") {
        row.lookups++;
        row.qtimes.push(at);
      }
      row.qtype ||= str(f.qtype);
      continue;
    }
    row.qtype ||= str(f.rtype);
    const ip = str(f.ip).trim();
    const name = lower(str(f.name));
    if (name && name !== owner && !row.via.includes(name)) row.via.push(name);
    if (ip) {
      row.ips.add(ip);
      (answers.get(ip) ?? answers.set(ip, []).get(ip)!).push({ name: owner, at });
    }
  }
  const nameFor = (ip: string, when: number): string | null => {
    const list = answers.get(ip);
    if (!list?.length) return null;
    let best = list[0];
    for (const a of list) if (a.at <= when + 1000) best = a;
    return best.name;
  };

  // Suricata alerts (NIDS worker → eventbus → job_events) → index by
  // dest_ip and dest_ip:port. Rows attach sigs when their ip (and port
  // when known) match. Dedup by signature_id so a single rule firing
  // on many packets of the same flow shows as one tag per row.
  const sigsByIp = new Map<string, SigRef[]>();
  const sigsByIpPort = new Map<string, SigRef[]>();
  for (const e of events) {
    if (e.kind !== "suricata.alert") continue;
    const f = e.fields ?? {};
    const dstIp = str(f.dest_ip);
    if (!dstIp) continue;
    const sig: SigRef = {
      signatureId: num(f.signature_id) ?? 0,
      signature: str(f.signature) || "unknown",
      category: str(f.category),
      severity: num(f.severity) ?? 3,
      appProto: str(f.app_proto) || undefined,
      flowId: num(f.flow_id),
      destPort: num(f.dest_port),
    };
    const pushIf = (bucket: Map<string, SigRef[]>, key: string) => {
      const list = bucket.get(key) ?? [];
      if (!list.some((s) => s.signatureId === sig.signatureId)) list.push(sig);
      bucket.set(key, list);
    };
    pushIf(sigsByIp, dstIp);
    if (sig.destPort !== undefined) pushIf(sigsByIpPort, `${dstIp}|${sig.destPort}`);
  }
  const sortSigs = (list: SigRef[]) => list.slice().sort((a, b) => a.severity - b.severity || a.signature.localeCompare(b.signature));
  const sigsFor = (ip: string, port?: number): SigRef[] => {
    if (port !== undefined) {
      const exact = sigsByIpPort.get(`${ip}|${port}`);
      if (exact?.length) return sortSigs(exact);
    }
    return sortSigs(sigsByIp.get(ip) ?? []);
  };

  const hosts = new Map<string, HostAcc>();
  const hostFor = (key: string, host: string | null): HostAcc => {
    let h = hosts.get(key);
    if (!h) {
      h = {
        key,
        host,
        addrs: new Set(),
        ports: new Set(),
        procs: new Map(),
        reqs: new Map(),
        conn: 0,
        flow: 0,
        sent: 0,
        received: 0,
        maxSent: 0,
        contacts: [],
        seqs: [],
        schemes: new Set(),
        inScope: false,
        outOfScope: false,
      };
      hosts.set(key, h);
    }
    return h;
  };
  const credit = (h: HostAcc, pid: number | undefined, seq: number) => {
    if (pid === undefined) return;
    const p = h.procs.get(pid) ?? { name: procName(pid, seq) ?? "", pid, count: 0 };
    p.count++;
    h.procs.set(pid, p);
    if (inScope(pid, seq)) h.inScope = true;
    else h.outOfScope = true;
  };

  type HttpAcc = HttpRow & { times: number[]; pids: Map<number, number> };
  const httpRows = new Map<string, HttpAcc>();
  const connRows = new Map<string, ConnRow & { times: number[] }>();
  const flowRows = new Map<string, FlowRow>();

  at = firstAt;
  for (const e of events) {
    at = tsOf(e, at);
    const t = at - t0;
    const f = e.fields ?? {};
    switch (e.kind) {
      case "net.http": {
        const { host: rawHost, port } = splitHostPort(str(f.host));
        let host = lower(rawHost);
        if (!host) break;
        const scheme = str(f.scheme).toLowerCase();
        const p = port ?? (scheme === "https" ? 443 : scheme === "http" ? 80 : undefined);
        if (isIp(host)) host = nameFor(host, at) ?? host;
        const h = hostFor(host, isIp(host) ? null : host);
        if (isIp(rawHost)) h.addrs.add(rawHost);
        if (p !== undefined) h.ports.add(p);
        if (scheme) h.schemes.add(scheme);
        const method = str(f.method).toUpperCase() || "GET";
        const path = str(f.path) || "/";
        const status = num(f.status);
        const sent = num(f.req_bytes) ?? 0;
        const received = num(f.resp_bytes) ?? 0;
        const sized = f.req_bytes != null || f.resp_bytes != null;
        const rk = `${method} ${path}`;
        const req = h.reqs.get(rk) ?? { method, scheme: scheme || undefined, path, status, count: 0, sent: 0, received: 0, firstT: t, times: [] };
        req.count++;
        req.sent += sent;
        req.received += received;
        if (status !== undefined) req.status = status;
        req.times.push(at);
        h.reqs.set(rk, req);
        h.sent += sent;
        h.received += received;
        h.maxSent = Math.max(h.maxSent, sent);
        h.contacts.push(t);
        h.seqs.push(e.seq);
        const pid = links.httpPid.get(e.seq);
        credit(h, pid, e.seq);

        const key = `${method}|${host}|${path}|${scheme}`;
        const row: HttpAcc = httpRows.get(key) ?? {
          key,
          method,
          scheme,
          host: str(f.host),
          path,
          url: `${scheme ? `${scheme}://` : ""}${str(f.host)}${path}`,
          statuses: [],
          count: 0,
          sent: 0,
          received: 0,
          sized: false,
          firstT: t,
          process: null,
          background: false,
          seqs: [],
          times: [],
          pids: new Map(),
          sigs: isIp(host) ? sigsFor(host, p) : [],
        };
        row.count++;
        row.sent += sent;
        row.received += received;
        row.sized ||= sized;
        if (status !== undefined && !row.statuses.includes(status)) row.statuses.push(status);
        row.seqs.push(e.seq);
        row.times.push(at);
        if (pid !== undefined) row.pids.set(pid, (row.pids.get(pid) ?? 0) + 1);
        // Keep the most recent body sample seen for this row. The proxy
        // caps each direction at 64 KiB; truncated=true flags that the
        // full body was bigger (reqBodyFullSize holds the true total).
        const reqB64 = typeof f.req_body_b64 === "string" ? f.req_body_b64 : undefined;
        const respB64 = typeof f.resp_body_b64 === "string" ? f.resp_body_b64 : undefined;
        if (reqB64) {
          row.reqBodyB64 = reqB64;
          row.reqBodyTruncated = f.req_body_truncated === true;
          if (f.req_bytes != null) row.reqBodyFullSize = Number(f.req_bytes);
        }
        if (respB64) {
          row.respBodyB64 = respB64;
          row.respBodyTruncated = f.resp_body_truncated === true;
          if (f.resp_bytes != null) row.respBodyFullSize = Number(f.resp_bytes);
        }
        httpRows.set(key, row);
        break;
      }
      case "net.conn": {
        const dst = str(f.dst).trim();
        const { host: ip, port } = splitHostPort(dst);
        if (!ip) break;
        const name = nameFor(ip, at);
        const h = hostFor(name ?? ip, name);
        h.addrs.add(ip);
        if (port !== undefined) h.ports.add(port);
        h.conn++;
        h.contacts.push(t);
        h.seqs.push(e.seq);
        const pid = num(f.pid);
        credit(h, pid, e.seq);

        const key = `${pid ?? ""}|${dst.toLowerCase()}|${str(f.op)}`;
        const row = connRows.get(key) ?? {
          key,
          pid,
          name: pid !== undefined ? procName(pid, e.seq) : undefined,
          dst,
          ip,
          port,
          host: name,
          proto: str(f.proto) || "tcp",
          op: str(f.op) || "connect",
          count: 0,
          firstT: t,
          mirrored: 0,
          background: false,
          seqs: [],
          times: [],
          sigs: sigsFor(ip, port),
        };
        row.count++;
        if (links.mirrored.has(e.seq)) row.mirrored++;
        row.seqs.push(e.seq);
        row.times.push(at);
        connRows.set(key, row);
        break;
      }
      case "net.flow": {
        const { host: ip, port } = splitHostPort(str(f.dst));
        if (!ip) break;
        const dport = num(f.dport) ?? port;
        const name = nameFor(ip, at);
        const mirror = links.mirrorOf.get(e.seq);
        const h = hostFor(name ?? ip, name);
        h.addrs.add(ip);
        if (dport !== undefined) h.ports.add(dport);
        h.flow++;
        h.seqs.push(e.seq);
        const len = num(f.len);
        if (len !== undefined) h.sent += len;
        if (!mirror) h.contacts.push(t);

        const proto = str(f.proto).toLowerCase() || "ip";
        const key = `${proto}|${ip}|${dport ?? ""}`;
        const row = flowRows.get(key) ?? { key, proto, ip, dport, src: str(f.src), count: 0, bytes: 0, sized: false, firstT: t, mirrored: 0, host: name, background: false, seqs: [], sigs: sigsFor(ip, dport) };
        row.count++;
        if (len !== undefined) {
          row.bytes += len;
          row.sized = true;
        }
        if (mirror) row.mirrored++;
        row.seqs.push(e.seq);
        flowRows.set(key, row);
        break;
      }
      default:
        break;
    }
  }

  // A resolved name is "contacted" when an address it resolved to was reached.
  const reached = new Set<string>();
  for (const h of hosts.values()) for (const a of h.addrs) reached.add(a);

  const hostRows: HostLedgerRow[] = [...hosts.values()].map((h) => {
    const dnsRow = h.host ? dnsRows.get(h.host) : undefined;
    const seqs = [...h.seqs, ...(dnsRow?.seqs ?? [])].sort((a, b) => a - b);
    const { background, service } = bgOf(seqs, noise);
    const contacts = dedupe(h.contacts);
    const procs = [...h.procs.values()].sort((a, b) => b.count - a.count);
    const requests = [...h.reqs.values()]
      .map(({ times, ...r }) => ({ ...r, cadenceSec: cadenceOf(times) }))
      .sort((a, b) => b.count - a.count || a.firstT - b.firstT);
    let citeSev: Severity | undefined;
    const citeTags = new Set<string>();
    if (opts.cite) {
      for (const s of seqs) {
        const c = opts.cite.get(s);
        if (!c) continue;
        if (!citeSev || c.sev > citeSev) citeSev = c.sev;
        c.tagIds.forEach((id) => citeTags.add(id));
      }
    }
    const httpTimes = [...h.reqs.values()].flatMap((r) => r.times);
    const cadence = httpTimes.length >= 3 ? cadenceOf(httpTimes) : cadenceOf(contacts.map((c) => c + t0));
    const hostSigs: SigRef[] = [];
    const seenSigId = new Set<number>();
    for (const addr of h.addrs) {
      for (const s of sigsFor(addr)) {
        if (seenSigId.has(s.signatureId)) continue;
        seenSigId.add(s.signatureId);
        hostSigs.push(s);
      }
    }
    hostSigs.sort((a, b) => a.severity - b.severity || a.signature.localeCompare(b.signature));
    return {
      key: h.key,
      host: h.host,
      addrs: [...h.addrs],
      ports: [...h.ports].sort((a, b) => a - b),
      process: procs.length ? { name: procs[0].name, pid: procs[0].pid } : null,
      requests,
      connCount: h.conn,
      flowCount: h.flow,
      sent: h.sent,
      received: h.received,
      firstT: contacts[0] ?? 0,
      lastT: contacts[contacts.length - 1] ?? 0,
      contacts,
      cadenceSec: cadence,
      background,
      service,
      seqs,
      via: dnsRow?.via ?? [],
      lookups: dnsRow?.lookups ?? 0,
      procs,
      party: h.inScope ? "sample" : h.outOfScope ? "other" : "none",
      httpCount: requests.reduce((s, r) => s + r.count, 0),
      schemes: [...h.schemes],
      maxSent: h.maxSent,
      citeSev,
      citeTags: [...citeTags],
      sigs: hostSigs,
    };
  });
  hostRows.sort((a, b) => Number(a.background) - Number(b.background) || b.contacts.length - a.contacts.length || a.firstT - b.firstT);

  const http: HttpRow[] = [...httpRows.values()]
    .map(({ times, pids, ...r }) => {
      const top = [...pids].sort((a, b) => b[1] - a[1])[0];
      return { ...r, cadenceSec: cadenceOf(times), process: top ? { pid: top[0], name: procName(top[0], r.seqs[0]) ?? "" } : null, ...bgOf(r.seqs, noise) };
    })
    .sort((a, b) => Number(a.background) - Number(b.background) || b.count - a.count || a.firstT - b.firstT);

  const dns: DnsRow[] = [...dnsRows.values()]
    .map(({ qtimes, ips, ...r }) => ({ ...r, answers: [...ips], lookups: Math.max(1, r.lookups), cadenceSec: cadenceOf(qtimes), contacted: [...ips].some((ip) => reached.has(ip)), ...bgOf(r.seqs, noise) }))
    .sort((a, b) => Number(a.background) - Number(b.background) || b.lookups - a.lookups || a.firstT - b.firstT);

  const conns: ConnRow[] = [...connRows.values()]
    .map(({ times, ...r }) => ({ ...r, cadenceSec: cadenceOf(times), ...bgOf(r.seqs, noise) }))
    .sort((a, b) => Number(a.background) - Number(b.background) || b.count - a.count || a.firstT - b.firstT);

  const flows: FlowRow[] = [...flowRows.values()]
    .map((r) => ({ ...r, ...bgOf(r.seqs, noise) }))
    .sort((a, b) => Number(a.background) - Number(b.background) || b.count - a.count || a.firstT - b.firstT);

  return { hosts: hostRows, http, dns, conns, flows };
}

export function buildHosts(events: readonly Event[], opts: BuildHostsOptions = {}): HostLedgerRow[] {
  return buildLedger(events, opts).hosts;
}

// ---- the host's ThreatTag ---------------------------------------------------------------------------

type HostSignal = { kind: "upload" | "beacon" | "port" | "direct" | "plaintext" | "sample"; label: string };

const COMMON_PORTS = new Set([80, 443]);

/** Facts that make a non-background host stand out, most telling first (at most two are shown). */
export function hostSignals(row: HostLedgerRow): HostSignal[] {
  if (row.background || row.party === "other") return [];
  const out: HostSignal[] = [];
  if (row.maxSent >= 16 * 1024 || (row.sent >= 32 * 1024 && row.sent > 8 * row.received)) out.push({ kind: "upload", label: `upload ${bytes(row.maxSent || row.sent)}` });
  if (row.cadenceSec && row.contacts.length >= 4) out.push({ kind: "beacon", label: `beacon ${cadenceText(row.cadenceSec)}` });
  const odd = row.ports.filter((p) => !COMMON_PORTS.has(p));
  if (odd.length) out.push({ kind: "port", label: `${odd.length === 1 ? "port" : "ports"} ${odd.slice(0, 2).join(", ")}` });
  if (!row.host) out.push({ kind: "direct", label: "no DNS name" });
  if (row.schemes.includes("http")) out.push({ kind: "plaintext", label: "plaintext HTTP" });
  if (!out.length) out.push({ kind: "sample", label: row.party === "sample" ? "sample traffic" : "unattributed" });
  return out;
}

// ---- report context (memoized per job on snap.version) ------------------------------------------------

const LEDGERS = new Map<string, { version: number; cite?: CiteIndex; t0: number | null; mode: string; sample?: string; ledger: NetLedger }>();

/** The run's ledger: background from the report's index, names from the event stream, cites in documents only. */
export function ledgerOf(ctx: ReportCtx): NetLedger {
  const cite = ctx.mode === "live" ? undefined : ctx.cite;
  const t0 = t0Of(ctx);
  const version = ctx.stream.snap.version;
  const sample = ctx.job.filename;
  const m = LEDGERS.get(ctx.job.id);
  if (m && m.version === version && m.cite === cite && m.t0 === t0 && m.mode === ctx.mode && m.sample === sample) return m.ledger;
  const ledger = buildLedger(ctx.stream.snap.events, { t0, noise: noiseOf(ctx), procName: namesOf(ctx), cite, sample });
  LEDGERS.delete(ctx.job.id);
  LEDGERS.set(ctx.job.id, { version, cite, t0, mode: ctx.mode, sample, ledger });
  if (LEDGERS.size > 8) LEDGERS.delete(LEDGERS.keys().next().value!);
  return ledger;
}
